import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import {
  Sparkles,
  ChevronDown,
  Wand2,
  Palette,
  Share2,
  Calendar,
  BarChart3,
  Eye,
  MessageCircle,
  Users,
  Target,
  Hash,
  Globe,
  TrendingUp,
  Box,
  Zap,
  Image,
  Video,
  Layers3,
  UserPlus,
  Shield,
  Menu,
  X
} from 'lucide-react';

interface NavbarProps {
  activePage?: 'products' | 'pricing';
}

const Navbar: React.FC<NavbarProps> = ({ activePage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleMobileMenuToggle = () => {
    console.log('Mobile menu toggle clicked, current state:', isMobileMenuOpen);
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Safely get authentication status with fallback
  let isAuthenticated = false;
  try {
    const auth = useAuth();
    isAuthenticated = auth?.isAuthenticated || false;
  } catch (error) {
    // If AuthContext is not available, assume user is not authenticated
    isAuthenticated = false;
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const header = document.querySelector('header');
      if (isMobileMenuOpen && header && !header.contains(target)) {
        console.log('Closing mobile menu due to outside click');
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      // Add a small delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isMobileMenuOpen]);

  // Products data structure with conditional links
  const getServiceLink = (appLink: string, sectionId: string) => {
    return isAuthenticated ? appLink : `/#${sectionId}`;
  };

  const productCategories = [
    {
      title: "Content Generation",
      icon: Wand2,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      subcategories: [
        {
          title: "Image Studio",
          icon: Image,
          color: "text-blue-500",
          bgColor: "bg-blue-100",
          services: [
            { name: "Multiple Style Generation", description: "Create diverse visual styles", link: getServiceLink("/app/ad-studio", "content-creation") },
            { name: "Multi-Product Creation", description: "Generate content for multiple products", link: getServiceLink("/app/ad-studio", "content-creation") },
            { name: "Brandify", description: "Apply brand guidelines automatically", link: getServiceLink("/app/ad-studio", "content-creation") }
          ]
        },
        {
          title: "AR Studio",
          icon: Layers3,
          color: "text-purple-500",
          bgColor: "bg-purple-100",
          services: [
            { name: "3D Model Generation", description: "Create 3D models from product images", link: getServiceLink("/app/ad-studio-ar", "content-creation") },
            { name: "AR content", description: "Interactive AR content", link: getServiceLink("/app/ad-studio-ar", "content-creation") },
            { name: "AR Viewer", description: "Interactive 3D product previews", link: getServiceLink("/app/ad-studio-ar", "content-creation") }
          ]
        },
        {
          title: "Video Studio",
          icon: Video,
          color: "text-green-500",
          bgColor: "bg-green-100",
          services: [
            { name: "AI Video Generation", description: "Create engaging video content", link: getServiceLink("/app/ad-studio-video", "content-creation") },
            { name: "Dynamic Video content", description: "Personalized video content", link: getServiceLink("/app/ad-studio-video", "content-creation") }
          ]
        }
      ]
    },
    {
      title: "Multi-Platform Publishing",
      icon: Share2,
      color: "text-green-600",
      bgColor: "bg-green-50",
      services: [
        { name: "Automatic Multi-Platform Posts", description: "Adapt content for all platforms", link: getServiceLink("/app/posts", "scheduling") },
        { name: "Multi Content Type Creation", description: "Images, videos, carousels", link: getServiceLink("/app/posts", "scheduling") },
        { name: "AI Caption Generation", description: "Brand-customized captions", link: getServiceLink("/app/posts", "scheduling") },
        { name: "Smart Hashtag Generation", description: "Optimized hashtag suggestions", link: getServiceLink("/app/posts", "scheduling") }
      ]
    },
    {
      title: "Scheduling & Posting",
      icon: Calendar,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      services: [
        { name: "Multi-Platform Publishing", description: "Post to all platforms simultaneously", link: getServiceLink("/app/posts", "scheduling") },
        { name: "Optimized Scheduling", description: "AI-powered optimal timing", link: getServiceLink("/app/posts", "scheduling") },
        { name: "Unified Calendar", description: "Centralized content calendar", link: getServiceLink("/app/posts", "scheduling") }
      ]
    },
    {
      title: "Analytics & Insights",
      icon: BarChart3,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      services: [
        { name: "Multi-Platform Connections", description: "Connect all social accounts", link: getServiceLink("/app/dashboard", "analytics") },
        { name: "Engagement Tracking", description: "Monitor growth and engagement", link: getServiceLink("/app/dashboard", "analytics") },
        { name: "Social Health Score", description: "Overall performance metrics", link: getServiceLink("/app/dashboard", "analytics") },
        { name: "Sentiment Analysis", description: "Audience sentiment insights", link: getServiceLink("/app/dashboard", "analytics") },
        { name: "Unified Inbox", description: "Manage all platform messages", link: getServiceLink("/app/dashboard", "analytics") },
        { name: "Performance Analytics", description: "Best performing content analysis", link: getServiceLink("/app/dashboard", "analytics") }
      ]
    },
    {
      title: "Team Collaboration",
      icon: Users,
      color: "text-teal-600",
      bgColor: "bg-teal-50",
      services: [
        { name: "Client Management", description: "Organize and manage multiple clients", link: getServiceLink("/app/clients", "team-collaboration") },
        { name: "Team Workspaces", description: "Collaborative workspaces for teams", link: getServiceLink("/app/team", "team-collaboration") },
        { name: "Role-Based Permissions", description: "Control team access & permissions", link: getServiceLink("/app/team", "team-collaboration") }
      ]
    }
  ];

  return (
    <header className={`sticky top-0 z-50 transition-shadow duration-300 ${isScrolled ? 'shadow-md' : ''} bg-white border-b border-gray-200/50`}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 md:py-4">
          <Link to="/" className="flex items-center">
            <img
              src="/logo-light.png"
              alt="Quixocial"
              className="h-8 md:h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Home Link - Only show when not on home page */}
            {activePage !== 'home' && (
              <Link to="/">
                <Button variant="ghost" className="text-lg text-gray-600 hover:bg-transparent hover:text-gray-900">
                  Home
                </Button>
              </Link>
            )}

            {/* Products Dropdown */}
            <div className="relative dropdown-container"
              onMouseLeave={() => {
                // Clear any existing timeout
                if (dropdownTimeout) {
                  clearTimeout(dropdownTimeout);
                }
                // Set a new timeout to close the dropdown
                const timeout = setTimeout(() => setIsProductsOpen(false), 150);
                setDropdownTimeout(timeout);
              }}
              onMouseEnter={() => {
                // Clear timeout when mouse enters the container
                if (dropdownTimeout) {
                  clearTimeout(dropdownTimeout);
                  setDropdownTimeout(null);
                }
                setIsProductsOpen(true);
              }}>
              <Button
                variant="ghost"
                className={`text-lg ${activePage === 'products' ? 'text-gray-900' : 'text-gray-600'} hover:bg-transparent hover:text-gray-900 flex items-center space-x-1`}
                onClick={() => setIsProductsOpen(!isProductsOpen)}
              >
                <span>Products</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`} />
              </Button>

              {/* Enhanced Desktop Dropdown Menu */}
              {isProductsOpen && (
                <>
                  {/* Invisible bridge to prevent hover gaps */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-full h-2 bg-transparent"></div>
                  <div
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 navbar-dropdown bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
                    style={{
                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      maxHeight: '780px'
                    }}
                    onMouseEnter={() => setIsProductsOpen(true)}
                  >
                    {/* Modern Dropdown Header */}
                    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-3 relative overflow-hidden">
                      {/* Animated Background Elements */}
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/15 via-purple-600/15 to-cyan-600/15"></div>
                      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>

                      {/* Floating Orbs */}
                      <div className="absolute top-2 right-4 w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-xl"></div>
                      <div className="absolute bottom-2 left-6 w-8 h-8 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-lg"></div>

                      <div className="relative z-10">
                        <h2 className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent mb-1">
                          Our Products
                        </h2>
                        <p className="text-slate-300/90 text-sm font-medium">Everything you need to create, manage, and analyze your content</p>
                      </div>
                    </div>

                    <div className="p-3 pb-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {/* Column 1: Content Generation with Subcategories */}
                        <div className="space-y-2 lg:col-span-1 xl:col-span-1">
                          {/* Modern Category Header */}
                          <div className="flex items-center space-x-3 pb-3 mb-3 border-b border-gradient-to-r from-blue-200/50 via-purple-200/50 to-cyan-200/50 relative">
                            <div className={`w-9 h-9 ${productCategories[0].bgColor} rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group relative overflow-hidden`}>
                              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                              {React.createElement(productCategories[0].icon, { className: `w-5 h-5 ${productCategories[0].color} relative z-10 group-hover:scale-110 transition-transform duration-300` })}
                            </div>
                            <div>
                              <h3 className="font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent text-base lg:text-lg">{productCategories[0].title}</h3>
                              <p className="text-sm text-gray-500/80 font-medium">AI-powered tools</p>
                            </div>
                            {/* Subtle glow line */}
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent"></div>
                          </div>

                          {/* Compact Subcategories */}
                          {productCategories[0].subcategories?.map((subcategory, subIndex) => (
                            <div key={subcategory.title} className="space-y-1">
                              {/* Modern Subcategory Header */}
                              <div className="flex items-center space-x-2 ml-3 group">
                                <div className={`w-4 h-4 ${subcategory.bgColor} rounded-md flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-200 hover:scale-105`}>
                                  {React.createElement(subcategory.icon, { className: `w-2.5 h-2.5 ${subcategory.color}` })}
                                </div>
                                <h4 className="font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent text-sm group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-200">{subcategory.title}</h4>
                              </div>

                              {/* Enhanced Subcategory Services */}
                              <div className="space-y-1 ml-8">
                                {subcategory.services.map((service, serviceIndex) => (
                                  <Link
                                    key={service.name}
                                    to={service.link}
                                    className="block p-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group relative overflow-hidden border border-transparent hover:border-blue-200/30 hover:shadow-sm"
                                    onClick={(e) => {
                                      setIsProductsOpen(false);
                                      // Handle hash links for same page navigation
                                      if (service.link.startsWith('/#')) {
                                        e.preventDefault();
                                        const sectionId = service.link.substring(2);
                                        const element = document.getElementById(sectionId);
                                        if (element) {
                                          element.scrollIntoView({ behavior: 'smooth' });
                                        }
                                      }
                                    }}
                                  >
                                    {/* Hover effect background */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                                    <div className="flex items-start space-x-2 relative z-10">
                                      <div className="flex-1">
                                        <h5 className="text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                                          {service.name}
                                        </h5>
                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2 hidden lg:block">
                                          {service.description}
                                        </p>
                                      </div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Column 2: Multi-Platform Publishing + Scheduling & Posting */}
                        <div className="space-y-2 lg:col-span-1 xl:col-span-1">
                          {/* Multi-Platform Publishing */}
                          <div className="space-y-1">
                            <div className="flex items-center space-x-3 pb-2 border-b border-gradient-to-r from-blue-200/50 via-purple-200/50 to-cyan-200/50 relative">
                              <div className={`w-7 h-7 ${productCategories[1].bgColor} rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group relative overflow-hidden`}>
                                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                {React.createElement(productCategories[1].icon, { className: `w-4 h-4 ${productCategories[1].color} relative z-10 group-hover:scale-110 transition-transform duration-300` })}
                              </div>
                              <div>
                                <h3 className="font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent text-base lg:text-lg">{productCategories[1].title}</h3>
                                <p className="text-sm text-gray-500/80 font-medium">Cross-platform distribution</p>
                              </div>
                              {/* Subtle glow line */}
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent"></div>
                            </div>
                            <div className="space-y-1 ml-8">
                              {productCategories[1].services.map((service, serviceIndex) => (
                                <Link
                                  key={service.name}
                                  to={service.link}
                                  className="block p-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group relative overflow-hidden border border-transparent hover:border-blue-200/30 hover:shadow-sm"
                                  onClick={(e) => {
                                    setIsProductsOpen(false);
                                    // Handle hash links for same page navigation
                                    if (service.link.startsWith('/#')) {
                                      e.preventDefault();
                                      const sectionId = service.link.substring(2);
                                      const element = document.getElementById(sectionId);
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth' });
                                      }
                                    }
                                  }}
                                >
                                  {/* Hover effect background */}
                                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                                  <div className="flex items-start space-x-2 relative z-10">
                                    <div className="flex-1">
                                      <h4 className="text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                                        {service.name}
                                      </h4>
                                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 hidden lg:block">
                                        {service.description}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>

                          {/* Scheduling & Posting */}
                          <div className="space-y-1">
                            <div className="flex items-center space-x-3 pb-2 border-b border-gradient-to-r from-blue-200/50 via-purple-200/50 to-cyan-200/50 relative">
                              <div className={`w-7 h-7 ${productCategories[2].bgColor} rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group relative overflow-hidden`}>
                                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                {React.createElement(productCategories[2].icon, { className: `w-4 h-4 ${productCategories[2].color} relative z-10 group-hover:scale-110 transition-transform duration-300` })}
                              </div>
                              <div>
                                <h3 className="font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent text-base lg:text-lg">{productCategories[2].title}</h3>
                                <p className="text-sm text-gray-500/80 font-medium">Smart timing & automation</p>
                              </div>
                              {/* Subtle glow line */}
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent"></div>
                            </div>
                            <div className="space-y-1 ml-8">
                              {productCategories[2].services.map((service, serviceIndex) => (
                                <Link
                                  key={service.name}
                                  to={service.link}
                                  className="block p-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group relative overflow-hidden border border-transparent hover:border-blue-200/30 hover:shadow-sm"
                                  onClick={(e) => {
                                    setIsProductsOpen(false);
                                    // Handle hash links for same page navigation
                                    if (service.link.startsWith('/#')) {
                                      e.preventDefault();
                                      const sectionId = service.link.substring(2);
                                      const element = document.getElementById(sectionId);
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth' });
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex items-start space-x-2">
                                    <div className="flex-1">
                                      <h4 className="text-xs lg:text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                                        {service.name}
                                      </h4>
                                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 hidden lg:block">
                                        {service.description}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Column 3: Analytics & Insights */}
                        <div className="space-y-2 lg:col-span-2 xl:col-span-1">
                          <div className="flex items-center space-x-3 pb-3 border-b border-gradient-to-r from-blue-200/50 via-purple-200/50 to-cyan-200/50 relative">
                            <div className={`w-7 h-7 ${productCategories[3].bgColor} rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group relative overflow-hidden`}>
                              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                              {React.createElement(productCategories[3].icon, { className: `w-4 h-4 ${productCategories[3].color} relative z-10 group-hover:scale-110 transition-transform duration-300` })}
                            </div>
                            <div>
                              <h3 className="font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent text-base lg:text-lg">{productCategories[3].title}</h3>
                              <p className="text-sm text-gray-500/80 font-medium">Performance tracking & data</p>
                            </div>
                            {/* Subtle glow line */}
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent"></div>
                          </div>
                          <div className="space-y-1 ml-8">
                            {productCategories[3].services.map((service, serviceIndex) => (
                              <Link
                                key={service.name}
                                to={service.link}
                                className="block p-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group relative overflow-hidden border border-transparent hover:border-blue-200/30 hover:shadow-sm"
                                onClick={(e) => {
                                  setIsProductsOpen(false);
                                  // Handle hash links for same page navigation
                                  if (service.link.startsWith('/#')) {
                                    e.preventDefault();
                                    const sectionId = service.link.substring(2);
                                    const element = document.getElementById(sectionId);
                                    if (element) {
                                      element.scrollIntoView({ behavior: 'smooth' });
                                    }
                                  }
                                }}
                              >
                                {/* Hover effect background */}
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                                <div className="flex items-start space-x-2 relative z-10">
                                  <div className="flex-1">
                                    <h4 className="text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                                      {service.name}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 hidden lg:block">
                                      {service.description}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>

                          {/* Team Collaboration */}
                          <div className="space-y-1 mt-3">
                            <div className="flex items-center space-x-3 pb-2 border-b border-gradient-to-r from-blue-200/50 via-purple-200/50 to-cyan-200/50 relative">
                              <div className={`w-7 h-7 ${productCategories[4].bgColor} rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group relative overflow-hidden`}>
                                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                {React.createElement(productCategories[4].icon, { className: `w-4 h-4 ${productCategories[4].color} relative z-10 group-hover:scale-110 transition-transform duration-300` })}
                              </div>
                              <div>
                                <h3 className="font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent text-base lg:text-lg">{productCategories[4].title}</h3>
                                <p className="text-sm text-gray-500/80 font-medium">Team management & workflow</p>
                              </div>
                              {/* Subtle glow line */}
                              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent"></div>
                            </div>
                            <div className="space-y-1 ml-8">
                              {productCategories[4].services.map((service, serviceIndex) => (
                                <Link
                                  key={service.name}
                                  to={service.link}
                                  className="block p-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group relative overflow-hidden border border-transparent hover:border-blue-200/30 hover:shadow-sm"
                                  onClick={(e) => {
                                    setIsProductsOpen(false);
                                    // Handle hash links for same page navigation
                                    if (service.link.startsWith('/#')) {
                                      e.preventDefault();
                                      const sectionId = service.link.substring(2);
                                      const element = document.getElementById(sectionId);
                                      if (element) {
                                        element.scrollIntoView({ behavior: 'smooth' });
                                      }
                                    }
                                  }}
                                >
                                  {/* Hover effect background */}
                                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                                  <div className="flex items-start space-x-2 relative z-10">
                                    <div className="flex-1">
                                      <h4 className="text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                                        {service.name}
                                      </h4>
                                      <p className="text-xs text-gray-500 mt-1 line-clamp-2 hidden lg:block">
                                        {service.description}
                                      </p>
                                    </div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>


                    </div>
                  </div>
                </>
              )}
            </div>
            <Link to="/pricing">
              <Button variant="ghost" className={`text-lg ${activePage === 'pricing' ? 'text-gray-900' : 'text-gray-600'} hover:bg-transparent hover:text-gray-900`}>
                Pricing
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-lg text-gray-600 hover:bg-transparent hover:text-gray-900">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="text-lg text-white"
                style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={handleMobileMenuToggle}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200/50 bg-white/95 backdrop-blur-sm" style={{ display: 'block' }}>
            <div className="px-4 py-4 space-y-3">
              {/* Mobile Products Section */}
              <div className="space-y-2">
                <div className="text-sm font-semibold text-gray-900 px-2">Products</div>
                <div className="space-y-1 pl-4">
                  <Link
                    to={getServiceLink("/app/ad-studio", "content-creation")}
                    className="block px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      const link = getServiceLink("/app/ad-studio", "content-creation");
                      if (link.startsWith('/#')) {
                        e.preventDefault();
                        const sectionId = link.substring(2);
                        const element = document.getElementById(sectionId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    Image Studio
                  </Link>
                  <Link
                    to={getServiceLink("/app/ad-studio-ar", "content-creation")}
                    className="block px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      const link = getServiceLink("/app/ad-studio-ar", "content-creation");
                      if (link.startsWith('/#')) {
                        e.preventDefault();
                        const sectionId = link.substring(2);
                        const element = document.getElementById(sectionId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    AR Studio
                  </Link>
                  <Link
                    to={getServiceLink("/app/ad-studio-video", "content-creation")}
                    className="block px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      const link = getServiceLink("/app/ad-studio-video", "content-creation");
                      if (link.startsWith('/#')) {
                        e.preventDefault();
                        const sectionId = link.substring(2);
                        const element = document.getElementById(sectionId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    Video Studio
                  </Link>
                  <Link
                    to={getServiceLink("/app/posts", "scheduling")}
                    className="block px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      const link = getServiceLink("/app/posts", "scheduling");
                      if (link.startsWith('/#')) {
                        e.preventDefault();
                        const sectionId = link.substring(2);
                        const element = document.getElementById(sectionId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    Multi-Platform Publishing
                  </Link>
                  <Link
                    to={getServiceLink("/app/dashboard", "analytics")}
                    className="block px-2 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      const link = getServiceLink("/app/dashboard", "analytics");
                      if (link.startsWith('/#')) {
                        e.preventDefault();
                        const sectionId = link.substring(2);
                        const element = document.getElementById(sectionId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    Analytics & Insights
                  </Link>
                </div>
              </div>

              {/* Mobile Navigation Links */}
              {activePage !== 'home' && (
                <Link
                  to="/"
                  className="block px-2 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
              )}
              <Link
                to="/pricing"
                className="block px-2 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/login"
                className="block px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="block mt-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Button 
                  className="w-full text-white"
                  style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
                >
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
